import { useEffect, useState } from "react";
import { memoryApi } from "../api/client";
import type { Memory } from "../types";
import "./MemoryView.css";
import { useToast } from "../lib/toast";

const CATEGORIES = ["general", "fact", "preference", "project"];

export default function MemoryView() {
  const toast = useToast();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");
  const [importance, setImportance] = useState(0.5);
  const [filter, setFilter] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editCategory, setEditCategory] = useState("general");
  const [editImportance, setEditImportance] = useState(0.5);

  const load = async () => {
    try {
      setMemories(await memoryApi.list(filter || undefined));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  useEffect(() => {
    load();
  }, [filter]);

  const add = async () => {
    if (!content.trim()) return;
    try {
      await memoryApi.add({ content, category, importance });
      setContent("");
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const remove = async (id: string) => {
    await memoryApi.remove(id);
    load();
  };

  const startEdit = (m: Memory) => {
    setEditingId(m.id);
    setEditContent(m.content);
    setEditCategory(m.category);
    setEditImportance(m.importance);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id: string) => {
    if (!editContent.trim()) return;
    try {
      await memoryApi.update(id, { content: editContent, category: editCategory, importance: editImportance });
      setEditingId(null);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">💭 Memory</h2>
          <p className="view-subtitle">Cross-chat memory store injected into context</p>
        </div>
        <select className="select" style={{ width: "auto" }} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="card memory-form">
        <div className="col">
          <textarea
            className="textarea"
            placeholder="Add a memory…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <div className="row" style={{ flexWrap: "wrap" }}>
            <select className="select" style={{ width: "auto" }} value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="row" style={{ gap: 8 }}>
              <span className="muted" style={{ fontSize: 12 }}>Importance: {importance.toFixed(1)}</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.1}
                value={importance}
                onChange={(e) => setImportance(Number(e.target.value))}
                style={{ accentColor: "var(--primary)" }}
              />
            </label>
            <div className="spacer" />
            <button className="btn btn-primary" onClick={add}>Add Memory</button>
          </div>
        </div>
      </div>

      <div className="memory-list">
        {memories.length === 0 && <div className="empty-state"><h3>No memories</h3></div>}
        {memories.map((m) => (
          <div key={m.id} className="card memory-item">
            {editingId === m.id ? (
              <div className="col">
                <textarea
                  className="textarea"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                />
                <div className="row" style={{ flexWrap: "wrap" }}>
                  <select className="select" style={{ width: "auto" }} value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <label className="row" style={{ gap: 8 }}>
                    <span className="muted" style={{ fontSize: 12 }}>Importance: {editImportance.toFixed(1)}</span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.1}
                      value={editImportance}
                      onChange={(e) => setEditImportance(Number(e.target.value))}
                      style={{ accentColor: "var(--primary)" }}
                    />
                  </label>
                  <div className="spacer" />
                  <button className="btn btn-sm" onClick={cancelEdit}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={() => saveEdit(m.id)}>Save</button>
                </div>
              </div>
            ) : (
              <>
                <div className="memory-item-content">{m.content}</div>
                <div className="row-between" style={{ marginTop: 10 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="tag tag-muted">{m.category}</span>
                    <span className="dim" style={{ fontSize: 11 }}>
                      importance {m.importance.toFixed(2)} · {new Date(m.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    <button className="btn btn-sm" onClick={() => startEdit(m)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => remove(m.id)}>Delete</button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}